import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { MdOutlineShowChart, MdOutlineTableRows } from "react-icons/md";
import { AppDispatch } from "../../store";
import { ChartDataPoint, ChartType } from "../../types/dashboardType";
import { fetchChartData } from "../../features/dashboard/DasboardServices";

const CHART_TYPES: { label: string; value: ChartType }[] = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Yearly", value: "yearly" },
];

// Categorical slots 1–3 (validated for colour-blind separation on white)
const LINE_CONFIG: { key: "sent" | "delivered" | "read"; color: string; label: string }[] = [
    { key: "sent", color: "#2a78d6", label: "Sent" },
    { key: "delivered", color: "#eb6834", label: "Delivered" },
    { key: "read", color: "#1baf7a", label: "Read" },
];

const ChartTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl bg-white border border-app-line shadow-pop px-3 py-2.5 min-w-[150px]">
            <p className="text-[12px] font-semibold text-app-ink mb-1.5">{label}</p>
            {LINE_CONFIG.map((l) => {
                const item = payload.find((p: any) => p.dataKey === l.key);
                return (
                    <div key={l.key} className="flex items-center justify-between gap-4 text-[12px] py-0.5">
                        <span className="flex items-center gap-1.5 text-app-muted">
                            <span className="w-2.5 h-[3px] rounded-full" style={{ backgroundColor: l.color }} />
                            {l.label}
                        </span>
                        <span className="font-semibold text-app-ink tabular-nums">
                            {Number(item?.value ?? 0).toLocaleString()}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default function PerformanceChart() {
    const dispatch = useDispatch<AppDispatch>();
    const [chartType, setChartType] = useState<ChartType>("daily");
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<"chart" | "table">("chart");

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);

            const result = await dispatch(fetchChartData(chartType));

            if (fetchChartData.fulfilled.match(result)) {
                const payload = result.payload;
                // Handle both array response and wrapped { data: [] } response
                const normalized = Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.data)
                        ? payload.data
                        : [];
                setChartData(normalized);
            } else if (fetchChartData.rejected.match(result)) {
                setError(result.payload ?? "Something went wrong");
                setChartData([]);
            }

            setLoading(false);
        };

        load();
    }, [chartType, dispatch]);

    const totals = useMemo(
        () =>
            LINE_CONFIG.reduce<Record<string, number>>((acc, l) => {
                acc[l.key] = chartData.reduce((sum, d) => sum + (Number(d[l.key]) || 0), 0);
                return acc;
            }, {}),
        [chartData]
    );

    return (
        <div className="relative bg-white rounded-3xl border border-app-line shadow-card p-5 sm:p-6 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h3 className="text-[15px] font-semibold text-app-ink">Message performance</h3>
                    <p className="text-[12.5px] text-app-muted mt-0.5">Sent, delivered and read over time</p>
                </div>

                <div className="flex items-center gap-2">
                    {/* Range */}
                    <div className="flex gap-0.5 bg-app-bg rounded-xl p-1" role="tablist" aria-label="Time range">
                        {CHART_TYPES.map((t) => (
                            <button
                                key={t.value}
                                role="tab"
                                aria-selected={chartType === t.value}
                                onClick={() => setChartType(t.value)}
                                className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-200 ${chartType === t.value
                                    ? "bg-white text-app-ink shadow-sm"
                                    : "text-app-muted hover:text-app-ink"
                                    }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Chart / table */}
                    <div className="flex gap-0.5 bg-app-bg rounded-xl p-1">
                        {([
                            ["chart", MdOutlineShowChart, "Chart view"],
                            ["table", MdOutlineTableRows, "Table view"],
                        ] as const).map(([v, Icon, label]) => (
                            <button
                                key={v}
                                onClick={() => setView(v)}
                                title={label}
                                aria-label={label}
                                aria-pressed={view === v}
                                className={`w-8 h-[30px] rounded-lg flex items-center justify-center transition-all ${view === v ? "bg-white text-app-ink shadow-sm" : "text-app-muted hover:text-app-ink"
                                    }`}
                            >
                                <Icon size={16} />
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Legend with totals for the selected range */}
            <div className="flex gap-x-6 gap-y-2 mb-4 flex-wrap">
                {LINE_CONFIG.map((l) => (
                    <div key={l.key} className="flex items-center gap-2">
                        <span className="w-3 h-[3px] rounded-full" style={{ backgroundColor: l.color }} />
                        <span className="text-[12.5px] text-app-muted">{l.label}</span>
                        <span className="text-[13px] font-semibold text-app-ink tabular-nums">
                            {loading ? "–" : totals[l.key].toLocaleString()}
                        </span>
                    </div>
                ))}
            </div>

            {/* Chart Area */}
            <div className="flex-1 min-h-[280px]">
                {loading ? (
                    <div className="h-full min-h-[280px] rounded-2xl bg-app-bg/60 animate-pulse" />
                ) : error ? (
                    <div className="flex items-center justify-center h-full min-h-[280px] text-red-500 text-sm">
                        {error}
                    </div>
                ) : chartData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[280px] text-center">
                        <span className="w-12 h-12 rounded-2xl bg-app-bg flex items-center justify-center text-slate-400 mb-3">
                            <MdOutlineShowChart size={22} />
                        </span>
                        <p className="text-[13.5px] font-medium text-app-ink">No data for this period</p>
                        <p className="text-[12px] text-app-muted mt-0.5">Messages you send will show up here.</p>
                    </div>
                ) : view === "table" ? (
                    <div className="app-scroll max-h-[300px] overflow-auto rounded-xl border border-app-line">
                        <table className="w-full text-[13px]">
                            <thead className="sticky top-0 bg-app-bg text-app-muted">
                                <tr>
                                    <th className="text-left font-medium px-4 py-2.5">Period</th>
                                    {LINE_CONFIG.map((l) => (
                                        <th key={l.key} className="text-right font-medium px-4 py-2.5">{l.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {chartData.map((d, i) => (
                                    <tr key={`${d.label}-${i}`} className="border-t border-app-line">
                                        <td className="px-4 py-2 text-app-ink">{d.label}</td>
                                        {LINE_CONFIG.map((l) => (
                                            <td key={l.key} className="px-4 py-2 text-right tabular-nums text-app-ink">
                                                {Number(d[l.key] ?? 0).toLocaleString()}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                            <defs>
                                {LINE_CONFIG.map((l) => (
                                    <linearGradient key={l.key} id={`fill-${l.key}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={l.color} stopOpacity={0.14} />
                                        <stop offset="100%" stopColor={l.color} stopOpacity={0} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid stroke="#eef1f5" vertical={false} />
                            <XAxis
                                dataKey="label"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                tickMargin={8}
                                minTickGap={16}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                width={48}
                            />
                            <Tooltip
                                content={<ChartTooltip />}
                                cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
                            />
                            {LINE_CONFIG.map((l) => (
                                <Area
                                    key={l.key}
                                    type="monotone"
                                    dataKey={l.key}
                                    name={l.label}
                                    stroke={l.color}
                                    strokeWidth={2}
                                    fill={`url(#fill-${l.key})`}
                                    dot={false}
                                    activeDot={{ r: 4.5, strokeWidth: 2, stroke: "#ffffff" }}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
